import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { supabase } from "../lib/supabaseClient";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import "./Activities.css";

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadActivities = async () => {
      setLoading(true);
      setError("");

      const { data, error } = await supabase
        .from("activities")
        .select(`
          id,
          title,
          slug,
          category,
          activity_date,
          location,
          description,
          cover_image,
          published
        `)
        .eq("published", true)
        .order("activity_date", { ascending: false });

      if (error) {
        console.error("Error loading activities:", error);
        setError("Unable to load activities right now.");
        setLoading(false);
        return;
      }

      setActivities(data || []);
      setLoading(false);
    };

    loadActivities();
  }, []);

  const formatDate = (date) => {
    if (!date) return null;

    return new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getShortDescription = (description) => {
    if (!description) return "";

    const maxLength = 145;

    if (description.length <= maxLength) {
      return description;
    }

    return `${description.slice(0, maxLength).trim()}...`;
  };

  return (
    <>
      <Navbar />
      <main className="activities-page">

      {/* =====================================================
          PAGE INTRO
      ===================================================== */}
      <section className="activities-hero">
        <div className="activities-container">

          <div className="activities-hero-label">
            <span></span>
            OUR ACTIVITIES
          </div>

          <h1>
            Beyond the
            <em>Clinic</em>
          </h1>

          <p>
            Explore the health initiatives, educational programs
            and community activities that are part of the
            Sudhamshu Ayurveda journey.
          </p>

        </div>
      </section>


      {/* =====================================================
          ACTIVITIES LIST
      ===================================================== */}
      <section className="activities-list-section">
        <div className="activities-container">

          {loading && (
            <div className="activities-state">
              <div className="activities-loader"></div>
              <p>Loading activities...</p>
            </div>
          )}


          {!loading && error && (
            <div className="activities-state activities-error">
              <span className="activities-state-label">
                SOMETHING WENT WRONG
              </span>

              <h2>We couldn't load the activities.</h2>

              <p>{error}</p>
            </div>
          )}


          {!loading && !error && activities.length === 0 && (
            <div className="activities-state activities-empty">

              <div className="activities-empty-mark">
                <span></span>
                <i>✦</i>
                <span></span>
              </div>

              <span className="activities-state-label">
                MORE TO COME
              </span>

              <h2>
                More activities are
                <em>on the way.</em>
              </h2>

              <p>
                We look forward to sharing upcoming health
                initiatives, community programs and meaningful
                moments from Sudhamshu Ayurveda.
              </p>

              <Link
                to="/contact"
                className="activities-empty-link"
              >
                Get in touch
                <span>→</span>
              </Link>

            </div>
          )}


          {!loading && !error && activities.length > 0 && (
            <>
              <div className="activities-heading-row">

                <div>
                  <span className="activities-section-label">
                    OUR JOURNEY
                  </span>

                  <h2>
                    Activities &amp;
                    <em>Initiatives</em>
                  </h2>
                </div>

                <span className="activities-count">
                  {activities.length}{" "}
                  {activities.length === 1 ? "Activity" : "Activities"}
                </span>

              </div>


              <div className="activities-grid">

                {activities.map((activity) => {
                  const formattedDate = formatDate(
                    activity.activity_date
                  );

                  return (
                    <article
                      key={activity.id}
                      className="activity-card"
                    >

                      {/* IMAGE */}
                      <Link
                        to={`/activities/${activity.slug}`}
                        className="activity-card-image-link"
                      >
                        <div className="activity-card-image">

                          {activity.cover_image ? (
                            <img
                              src={activity.cover_image}
                              alt={activity.title}
                              loading="lazy"
                            />
                          ) : (
                            <div className="activity-card-no-image">
                              <span>Sudhamshu Ayurveda</span>
                            </div>
                          )}

                          <div className="activity-card-image-overlay">
                            <span>
                              View Activity
                              <b>→</b>
                            </span>
                          </div>

                        </div>
                      </Link>


                      {/* CONTENT */}
                      <div className="activity-card-content">

                        <div className="activity-card-meta">

                          {activity.category && (
                            <span className="activity-card-category">
                              {activity.category}
                            </span>
                          )}

                          {formattedDate && (
                            <span className="activity-card-date">
                              {formattedDate}
                            </span>
                          )}

                        </div>


                        <h3>
                          <Link
                            to={`/activities/${activity.slug}`}
                          >
                            {activity.title}
                          </Link>
                        </h3>


                        {(activity.location ||
                          activity.description) && (
                          <div className="activity-card-details">

                            {activity.location && (
                              <span className="activity-card-location">
                                <span className="location-dot"></span>
                                {activity.location}
                              </span>
                            )}

                            {activity.description && (
                              <p>
                                {getShortDescription(
                                  activity.description
                                )}
                              </p>
                            )}

                          </div>
                        )}


                        <Link
                          to={`/activities/${activity.slug}`}
                          className="activity-card-link"
                        >
                          Read about the activity
                          <span>→</span>
                        </Link>

                      </div>

                    </article>
                  );
                })}

              </div>
            </>
          )}

        </div>
      </section>


      {/* =====================================================
          BOTTOM CTA
      ===================================================== */}
      {!loading && !error && activities.length > 0 && (
        <section className="activities-bottom-cta">
          <div className="activities-container">

            <div className="activities-bottom-inner">

              <div>
                <span className="activities-section-label">
                  STAY CONNECTED
                </span>

                <h2>
                  Be part of our
                  <em>journey.</em>
                </h2>
              </div>

              <Link
                to="/contact"
                className="activities-cta-button"
              >
                Contact Us
                <span>→</span>
              </Link>

            </div>

          </div>
        </section>
      )}

    </main>
    <Footer />
    </>  
  );
};

export default Activities;